// frontend/src/components/billing/v2-plans-section.tsx
// Read DESIGN.md before modifying this file.
// AIT-420 money model v2 — the capacity block for an org on the ACTIONS meter
// (trial cohort or a build/scale/business subscriber). It replaces the legacy
// tier grid + message meter inside PlansSection; everything below it
// (automated billing, top-up balance, add-card) is shared and stays in
// plans-section.tsx.
//
// Task 8 — the A2 state matrix is driven off the Stripe-native subscription
// `status` + `hasEverPaid` directly (trialing / paused / unpaid / past_due /
// canceled / incomplete_expired), NOT the older coarse `sub.trial.status`
// (not_started/active/expired) — that field stays on the wire only for
// back-compat. The trial branch renders BEFORE the paid-plan branch.
import { useState } from 'react';
import { toast } from 'sonner';
import NumberFlow from '@number-flow/react';
import { Info, Loader2, TriangleAlert } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { UsageProgress } from '@/components/usage-progress';
import { cn } from '@/lib/utils';
import { getV2Plan, isV2PlanSlug, nextV2PlanSlug, V2_PLANS } from '@/lib/plans';
import type { BillingEligibility } from '@/components/billing/queries';
import type { BillingSubscription } from '@/types/billing';

interface V2PlansSectionProps {
  sub: BillingSubscription;
  eligibility: BillingEligibility | null;
  /** From GET /organizations/:orgId/usage — days left in an active Stripe
   *  trial. null once converted, lapsed, or never trialed. */
  trialDaysLeft: number | null;
  billingInterval: 'monthly' | 'annual';
  /** Slug currently being sent to Stripe Checkout (spinner + disable). */
  checkoutSlug: string | null;
  onCheckout: (slug: string) => void;
  /** Opens the add-card dialog. `slug` is set when a plan card was clicked
   *  with no card on file (trial) — the parent remembers it and applies it
   *  the moment the card save succeeds, so the user never has to click the
   *  plan a second time. AIT-505 — the paused branch reuses this SAME prop:
   *  `slug` set from a "Resume on X" grid pick, omitted for the plain
   *  "Add a credit card" alert button; the resume itself happens server-side
   *  when the card is saved. */
  onAddCard: (slug?: 'build' | 'scale' | 'business') => void;
  onManage: () => void;
  /** "Save 17%": opens the shared approval modal quoting today's charge for
   *  switching this plan to yearly billing. Never charges directly. */
  onSwitchToAnnual: () => void;
  /** Opens the shared tier-switch approval modal (preview -> confirm -> charge)
   *  for build -> scale. Never charges directly. */
  onUpgrade: (slug: string, name: string) => void;
  /** In-trial plan swap: same subscription, no proration, trial_end untouched. */
  onSwapTrialPlan: (slug: 'build' | 'scale' | 'business') => void;
  /** past_due / unpaid: opens the hosted invoice for the open charge. */
  onPayInvoice: () => void;
}

// build < scale < business, by catalog order (mirrors V2_PLAN_RANK backend-side).
const V2_RANK: Record<'build' | 'scale' | 'business', number> = Object.fromEntries(
  V2_PLANS.map((p, i) => [p.slug, i]),
) as Record<'build' | 'scale' | 'business', number>;

function priceLabel(
  plan: { priceInCents: number; annualPriceInCents: number },
  interval: 'monthly' | 'annual',
): string {
  const cents = interval === 'annual' ? Math.floor(plan.annualPriceInCents / 12) : plan.priceInCents;
  return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}

/** One v2 plan in the SAME card design as the legacy tier grid (plans-section
 *  markup: NumberFlow price, yearly "17% off" pill, "billed annually" line,
 *  quota line, full-width CTA). `onAction` fires for every non-current card —
 *  the caller decides whether that means Checkout, an in-trial swap, or a
 *  paused resume. `isCurrent` renders a disabled "Current Plan" button. */
function EligiblePlanCard({
  slug,
  billingInterval,
  checkoutSlug,
  onAction,
  action = 'Start',
  recommended = false,
  isCurrent = false,
  isScheduled = false,
  joined = false,
  showDivider = false,
  showPopular = false,
}: {
  slug: 'build' | 'scale' | 'business';
  billingInterval: 'monthly' | 'annual';
  checkoutSlug: string | null;
  onAction: (slug: string) => void;
  action?: 'Start' | 'Choose' | 'Switch to' | 'Resume on' | 'Upgrade' | 'Downgrade';
  recommended?: boolean;
  /** This card is the org's current plan — CTA becomes a disabled "Current Plan". */
  isCurrent?: boolean;
  /** Already picked for when the trial ends. */
  isScheduled?: boolean;
  /** Rendered inside the joined V2PlanGrid container (border/radius come from it). */
  joined?: boolean;
  showDivider?: boolean;
  showPopular?: boolean;
}) {
  const plan = getV2Plan(slug);
  if (!plan) return null;
  // Bare verb, matching the legacy grid's CTA exactly: 'Upgrade' /
  // 'Downgrade' / 'Current Plan'. The plan name is already the card heading,
  // so repeating it in the button just makes it wrap.
  const cta = isScheduled
    ? 'Scheduled'
    : isCurrent
      ? 'Current Plan'
      : DIRECTIONAL_ACTIONS.has(action)
        ? action
        : `${action} ${plan.name}`;
  // Three cards can show the same bare verb, so the accessible name keeps the
  // plan: a screen reader hears "Upgrade to Scale", the button still reads
  // "Upgrade".
  const ctaLabel = isScheduled
    ? `${plan.name} starts when your trial ends`
    : isCurrent
      ? `${plan.name} is your current plan`
      : `${cta} ${cta === action ? plan.name : ''}`.trim();
  const isYearly = billingInterval === 'annual';
  const monthlyEquivalentCents = isYearly ? Math.round(plan.annualPriceInCents / 12) : plan.priceInCents;
  // Whole dollars like the legacy grid. Build's annual equivalent is $0.83 a
  // month — rounding shows $1 and the "billed annually" line carries the
  // exact charge.
  const priceDollars = Math.round(monthlyEquivalentCents / 100);
  return (
    <div
      className={cn(
        'relative flex flex-1 flex-col rounded-3xl border bg-background p-6',
        joined && 'lg:rounded-none lg:border-none lg:bg-transparent',
        showPopular && 'rounded-t-none max-lg:mt-7 max-lg:pt-4',
        !joined && recommended && 'border-primary',
      )}
    >
      {showPopular && (
        <div className="absolute inset-x-0 -top-7 flex h-7 items-center justify-center rounded-t-3xl rounded-b-none bg-primary px-3 text-xs font-semibold tracking-wide text-primary-foreground">
          Popular
        </div>
      )}

      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold tracking-tight">{plan.name}</span>
          {recommended && <Badge>Recommended</Badge>}
        </div>
        <span className="text-sm text-muted-foreground">{plan.tagline}</span>
      </div>

      <div className="mt-6 flex items-baseline gap-2" aria-label={`$${priceDollars}`}>
        <NumberFlow
          value={priceDollars}
          prefix="$"
          transformTiming={{ duration: 400, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
          spinTiming={{ duration: 600, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
          opacityTiming={{ duration: 250, easing: 'ease-out' }}
          className="text-4xl font-semibold tracking-tight"
        />
        <span
          className={`rounded-full bg-linear-to-r from-[#FA4941] to-[#FA7FA1] px-2.5 py-1 text-xs font-semibold text-white transition-all duration-300 ease-out ${
            isYearly ? 'scale-100 opacity-100' : 'pointer-events-none -translate-x-1 scale-90 opacity-0'
          }`}
        >
          17% off
        </span>
      </div>
      <span className="mt-1 text-xs text-muted-foreground">
        per month
        <span
          className={`inline-block transition-all duration-300 ease-out ${
            isYearly ? 'translate-x-0 opacity-100' : '-translate-x-1 opacity-0'
          }`}
        >
          , ${(plan.annualPriceInCents / 100).toLocaleString()} billed annually
        </span>
      </span>
      <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
        {plan.actions.toLocaleString()} actions/mo
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                aria-label="What counts as an action"
                className="inline-flex cursor-default text-muted-foreground/60 transition-colors hover:text-muted-foreground"
              />
            }
          >
            <Info className="size-3.5" />
          </TooltipTrigger>
          {/* Single child: the base TooltipContent is inline-flex, so multiple
              block children would flow side by side. */}
          <TooltipContent className="max-w-52">
            <div className="space-y-1 text-left">
              <p className="font-medium">Counts as an action:</p>
              <ul className="list-disc pl-4">
                <li>Sending a message</li>
                <li>Replying to a comment</li>
                <li>Hiding or unhiding a comment</li>
                <li>Publishing a post, reel, or story</li>
              </ul>
            </div>
          </TooltipContent>
        </Tooltip>
      </span>

      <Button
        size="lg"
        variant={isCurrent || isScheduled ? 'outline' : 'default'}
        className="mt-6 w-full"
        aria-label={ctaLabel}
        disabled={isCurrent || isScheduled || checkoutSlug !== null}
        onClick={() => onAction(plan.slug)}
      >
        {checkoutSlug === plan.slug && <Loader2 className="size-3.5 animate-spin" />}
        {cta}
      </Button>

      {showDivider && <div className="absolute right-0 top-6 bottom-6 hidden w-px bg-border lg:block" />}
    </div>
  );
}

/** Verbs that stand alone on the button, exactly as the legacy grid does. */
const DIRECTIONAL_ACTIONS = new Set(['Upgrade', 'Downgrade']);

/** All three v2 plans in the legacy joined grid (shared border on lg, divider
 *  between cards, "Popular" ribbon), always buyable. `recommendedSlug` (from
 *  measured eligibility) highlights one card and mutes the Popular ribbon so
 *  the two nudges never compete. `currentSlug` disables that one card as
 *  "Current Plan" (used while trialing/paused — the grid is the org's own
 *  plan picker, not a fresh acquisition surface). */
function V2PlanGrid({
  billingInterval,
  checkoutSlug,
  onAction,
  recommendedSlug = null,
  currentSlug = null,
  scheduledSlug = null,
  action,
}: {
  billingInterval: 'monthly' | 'annual';
  checkoutSlug: string | null;
  onAction: (slug: string) => void;
  recommendedSlug?: 'build' | 'scale' | 'business' | null;
  currentSlug?: 'build' | 'scale' | 'business' | null;
  /** The plan already picked for when the trial ends. Its card reads
   *  'Scheduled' and is disabled, so the choice is visible instead of the
   *  page looking like nothing happened. Any other card stays pickable, which
   *  is how a customer changes their mind. */
  scheduledSlug?: 'build' | 'scale' | 'business' | null;
  /** Overrides the per-card verb. The paused grid sets 'Resume on' for every
   *  card, because there the pick is which plan to come back on, not a move
   *  up or down from one you are currently using. */
  action?: 'Start' | 'Choose' | 'Switch to' | 'Resume on';
}) {
  const slugs = ['build', 'scale', 'business'] as const;
  // With a current plan on the row the grid is a plan PICKER, so each button
  // says which direction it moves you. Without one nothing is owned yet, so
  // every card is an acquisition and keeps the plain "Start".
  const verbFor = (
    slug: 'build' | 'scale' | 'business',
  ): 'Start' | 'Choose' | 'Switch to' | 'Resume on' | 'Upgrade' | 'Downgrade' =>
    action ?? (currentSlug === null ? 'Start' : V2_RANK[slug] > V2_RANK[currentSlug] ? 'Upgrade' : 'Downgrade');
  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:gap-0 lg:rounded-3xl lg:border lg:bg-background">
      {slugs.map((slug, idx) => (
        <EligiblePlanCard
          key={slug}
          slug={slug}
          billingInterval={billingInterval}
          checkoutSlug={checkoutSlug}
          onAction={onAction}
          action={verbFor(slug)}
          isScheduled={slug === scheduledSlug}
          isCurrent={slug === currentSlug && slug !== scheduledSlug}
          recommended={recommendedSlug === slug}
          joined
          showDivider={idx < slugs.length - 1}
          showPopular={recommendedSlug === null && getV2Plan(slug)?.popular === true}
        />
      ))}
    </div>
  );
}

export function V2PlansSection({
  sub,
  eligibility,
  trialDaysLeft,
  billingInterval,
  checkoutSlug,
  onCheckout,
  onAddCard,
  onManage,
  onSwitchToAnnual,
  onUpgrade,
  onSwapTrialPlan,
  onPayInvoice,
}: V2PlansSectionProps) {
  const currentPlan = isV2PlanSlug(sub.plan.slug) ? getV2Plan(sub.plan.slug) : undefined;
  // Picking a plan mid-trial is scheduled, not instant, but still a
  // deliberate choice rather than a stray click, so it is confirmed before
  // it fires.
  const [pendingSwap, setPendingSwap] = useState<{ slug: 'build' | 'scale' | 'business'; name: string } | null>(null);

  // --- Trialing: renders BEFORE the paid-plan branch. -----------------------
  if (sub.status === 'trialing' && currentPlan) {
    const scheduled = sub.pendingPlanChange;
    // The trial card must tell the user what happens next so the number is
    // never a surprise: a scheduled pick wins if one exists; otherwise, with
    // a card on file, the plan the org's usage has actually earned so far
    // (never Business by default); with no card, the pause warning instead.
    const earnedPlan = eligibility ? getV2Plan(eligibility.eligiblePlan) : undefined;
    return (
      <div className="space-y-4">
        <div className="rounded-2xl border bg-card p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                Free trial
              </span>
              {trialDaysLeft != null && <Badge variant="secondary">{trialDaysLeft} days left</Badge>}
            </div>
            {/* Only an offer while there is nothing on file. Once a card is
                saved — on its own, or as the confirmation of a plan pick —
                the line below already says what will be charged and when, so
                the button has nothing left to ask for. */}
            {sub.hasCard === false && <Button onClick={() => onAddCard()}>Add a credit card</Button>}
          </div>
          <h3 className="mt-3 text-2xl font-semibold tracking-tight">
            {sub.isCustomPlan ? sub.plan.name : currentPlan.name}
          </h3>
          {scheduled ? (
            <p className="mt-1 text-sm text-muted-foreground">
              You picked {scheduled.planName}. It starts on{' '}
              {new Date(scheduled.effectiveAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}
              , when your trial ends.
            </p>
          ) : sub.hasCard === false ? (
            <p className="mt-1 text-sm text-muted-foreground">
              Your channels will pause when your trial ends until you add a credit card.
            </p>
          ) : sub.isCustomPlan ? (
            // AIT-738: a custom trial converts on its negotiated tier and price
            // (the usage-based retarget skips it), so say exactly that.
            <p className="mt-1 text-sm text-muted-foreground">
              Your custom plan, {(sub.actionsQuota || currentPlan.actions).toLocaleString()} actions a month for{' '}
              {priceLabel(sub.plan, billingInterval)} per month, starts when your trial ends.
            </p>
          ) : earnedPlan ? (
            <p className="mt-1 text-sm text-muted-foreground">
              Based on your usage, {earnedPlan.name} starts at {priceLabel(earnedPlan, billingInterval)} per
              month when your trial ends.
            </p>
          ) : null}
          <div className="mt-6 border-t pt-4">
            <UsageProgress
              current={sub.actionsUsed ?? 0}
              limit={sub.actionsQuota ?? currentPlan.actions}
              label="Actions"
              labelInfo="Total actions across this organization. Includes sending a message, replying to a comment, hiding or unhiding a comment, and publishing a post, reel, or story."
              unitLabel="actions"
            />
          </div>
        </div>
        {/* AIT-738: a custom plan is negotiated; picking a catalog card would replace it. */}
        {!sub.isCustomPlan && (
          <>
            <p className="text-sm text-muted-foreground">Or switch plans now.</p>
            <V2PlanGrid
              billingInterval={billingInterval}
              checkoutSlug={checkoutSlug}
              onAction={(slug) => {
                const picked = getV2Plan(slug as 'build' | 'scale' | 'business');
                if (!picked) return;
                // No card on file: picking a plan needs one first. Open the
                // add-card dialog with the picked slug remembered — the parent
                // applies it automatically the moment the card save succeeds, no
                // second click needed.
                if (sub.hasCard === false) {
                  onAddCard(picked.slug);
                  return;
                }
                setPendingSwap({ slug: picked.slug, name: picked.name });
              }}
              // No currentSlug while trialing. Every trial is provisioned on
              // Business, but the customer never chose it -- it is the trial's
              // ceiling, not a plan they are on. Passing it disabled the Business
              // card as "Current Plan", so a customer who picked Build or Scale
              // could not pick Business afterwards or undo their choice, even
              // though swapTrialPlan accepts it (Codex, PR #218). `scheduledSlug`
              // alone marks the pick; every other card stays a live "Choose".
              currentSlug={null}
              action="Choose"
              scheduledSlug={(scheduled?.planSlug as 'build' | 'scale' | 'business' | undefined) ?? null}
            />
          </>
        )}

        {/* AIT-494 -- dismissing this dialog (X/backdrop/Escape) dropped the
            pick with zero feedback; 3 of the first 9 v2 converts hit it and
            one nearly paid the default plan's price for it. Confirm closes via
            setPendingSwap directly, so onOpenChange only ever means dismissal. */}
        <Dialog
          open={!!pendingSwap}
          onOpenChange={(open) => {
            if (!open) {
              setPendingSwap(null);
              toast.info('Your plan was not changed.');
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Switch to {pendingSwap?.name}</DialogTitle>
              <DialogDescription>
                {pendingSwap?.name} starts when your trial ends. You'll keep {currentPlan.name} and its
                actions until then, and nothing is charged today.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setPendingSwap(null); toast.info('Your plan was not changed.'); }}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (!pendingSwap) return;
                  const slug = pendingSwap.slug;
                  setPendingSwap(null);
                  onSwapTrialPlan(slug);
                }}
              >
                Confirm Switch
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // --- Paused. Usually no card on file when the trial ended, but NOT always: a
  // paying subscription can be paused from the Stripe dashboard, and telling
  // that customer their trial ended is simply false (Codex, PR #218). Branch on
  // whether they ever paid. AIT-505 — recovery is the SAME native AddCardDialog
  // as everywhere else (never Stripe Checkout): saving a card resumes the plan
  // server-side. An optional plan pick maps to onAddCard's slug. ------------
  if (sub.status === 'paused') {
    return (
      <div className="space-y-4">
        <Alert className="border-amber-500/30 bg-amber-500/5">
          <TriangleAlert className="text-amber-600 dark:text-amber-400" />
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>
              {sub.hasEverPaid
                ? 'Your subscription is paused. Add a credit card to resume.'
                : 'Your trial ended. Add a credit card to resume.'}
            </span>
            <Button variant="outline" size="sm" onClick={() => onAddCard()}>
              Add a credit card
            </Button>
          </AlertDescription>
        </Alert>
        {/* AIT-738: adding a card resumes a custom plan on its own terms. */}
        {!sub.isCustomPlan && (
          <>
            <p className="text-sm text-muted-foreground">Or pick a different plan to resume on.</p>
            <V2PlanGrid
              billingInterval={billingInterval}
              checkoutSlug={checkoutSlug}
              onAction={(slug) => onAddCard(slug as 'build' | 'scale' | 'business')}
              currentSlug={currentPlan?.slug ?? null}
              action="Resume on"
            />
          </>
        )}
      </div>
    );
  }

  // --- Unpaid: a real invoice went unpaid. Recovery is paying it, not
  // Checkout. Never rendered as Active. --------------------------------------
  if (sub.status === 'unpaid') {
    return (
      <div className="space-y-4">
        <Alert className="border-red-500/30 bg-red-500/5">
          <TriangleAlert className="text-red-600 dark:text-red-400" />
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>Payment past due. Pay your latest invoice to resume.</span>
            <Button variant="outline" size="sm" onClick={onPayInvoice}>
              Pay Invoice
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // --- Canceled or incomplete_expired: there is no subscription to manage,
  // whatever slug the row still carries. The ONLY branch that buys through
  // NORMAL subscription-mode Checkout — never the paused-resume action, never
  // rendered Active. The cancel path resets the slug to 'free' (which routes
  // a paid-then-canceled org to the legacy grid), but a drifted row (AIT-570:
  // canceled + 'build' + no Stripe sub) used to fall through to the paid card
  // below and offer a Manage Subscription button the backend 409s. -----------
  if (sub.status === 'canceled' || sub.status === 'incomplete_expired') {
    const recommendedSlug = eligibility?.eligiblePlan ?? null;
    return (
      <div className="space-y-4">
        <Alert className="border-amber-500/30 bg-amber-500/5">
          <TriangleAlert className="text-amber-600 dark:text-amber-400" />
          <AlertDescription>{sub.hasEverPaid ? 'Your plan ended.' : 'Your trial ended.'}</AlertDescription>
        </Alert>
        <p className="text-sm text-muted-foreground">
          Pick a plan to get started.{recommendedSlug ? ' Based on what you used, we recommend one.' : ''}
        </p>
        <V2PlanGrid
          billingInterval={billingInterval}
          checkoutSlug={checkoutSlug}
          onAction={onCheckout}
          recommendedSlug={recommendedSlug}
        />
      </div>
    );
  }

  // --- On a paid v2 plan (active or past_due): the actions meter + the next
  // tier's nudge. past_due additionally gets a payment-issue banner; the
  // plan card itself stays exactly as the current-plan card below. ----------
  if (currentPlan) {
    // AIT-738: a custom plan is negotiated; the next catalog tier may well be
    // smaller than it, so the honest next step is a conversation.
    const upsell = sub.isCustomPlan ? null : nextV2PlanSlug(currentPlan.slug);
    const upsellPlan = upsell ? getV2Plan(upsell) : undefined;
    return (
      <div className="space-y-4">
        {sub.status === 'past_due' && (
          <Alert className="border-red-500/30 bg-red-500/5">
            <TriangleAlert className="text-red-600 dark:text-red-400" />
            <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
              <span>Payment issue. Pay your latest invoice to keep your channels running.</span>
              <Button variant="outline" size="sm" onClick={onPayInvoice}>
                Pay Invoice
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {/* `incomplete` = the first payment declined or was never finished. The
            invoice is open and payable, and buying again is refused while this
            subscription is live, so this button is the only way out
            (Codex, PR #221). */}
        {sub.status === 'incomplete' && (
          <Alert className="border-amber-500/30 bg-amber-500/5">
            <TriangleAlert className="text-amber-600 dark:text-amber-400" />
            <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
              <span>Your payment did not go through. Finish it to start your plan.</span>
              <Button variant="outline" size="sm" onClick={onPayInvoice}>
                Finish Payment
              </Button>
            </AlertDescription>
          </Alert>
        )}
        <div className="rounded-2xl border bg-card p-6">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              Current plan
            </span>
            <Badge
              variant={
                sub.status === 'past_due' ? 'destructive' : sub.status === 'incomplete' ? 'secondary' : 'default'
              }
            >
              {sub.status === 'past_due' ? 'Past Due' : sub.status === 'incomplete' ? 'Payment Pending' : 'Active'}
            </Badge>
            {sub.billingInterval === 'annual' && <Badge variant="secondary">Yearly</Badge>}
          </div>
          <h3 className="mt-3 text-2xl font-semibold tracking-tight">
            {sub.isCustomPlan ? sub.plan.name : currentPlan.name}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {sub.isCustomPlan
              ? `${(sub.actionsQuota || currentPlan.actions).toLocaleString()} actions a month for ${priceLabel(sub.plan, billingInterval)} per month, tailored to you.`
              : currentPlan.tagline}
          </p>
          <div className="mt-6 border-t pt-4">
            <UsageProgress
              current={sub.actionsUsed ?? 0}
              limit={sub.actionsQuota || currentPlan.actions}
              label="Actions"
              labelInfo="Total actions across this organization. Includes sending a message, replying to a comment, hiding or unhiding a comment, and publishing a post, reel, or story."
              unitLabel="actions"
            />
          </div>
          <div className="mt-6 flex items-center gap-2 border-t pt-4">
            <Button variant="outline" size="sm" onClick={onManage}>
              Manage Subscription
            </Button>
            {/* Yearly = pay for 10 months, get 2 free; only a monthly
                subscriber has anything to gain — and only while nothing is
                owed: past_due/incomplete must settle their open invoice
                before an anchor-reset full-year invoice (Codex PR #260). */}
            {sub.billingInterval === 'monthly' && sub.status === 'active' && !sub.isCustomPlan && (
              <Button size="sm" onClick={onSwitchToAnnual}>
                Get 2 Months Free
              </Button>
            )}
          </div>
        </div>

        <div className="rounded-2xl border bg-card p-6">
          <h3 className="text-lg font-semibold tracking-tight">Need more room?</h3>
          {upsellPlan ? (
            <>
              <p className="mt-1 text-sm text-muted-foreground">
                {upsellPlan.name} gives you {upsellPlan.actions.toLocaleString()} actions a month for{' '}
                {priceLabel(upsellPlan, billingInterval)} per month. We show you what you pay today
                before anything is charged.
              </p>
              <Button
                size="lg"
                className="mt-6"
                aria-label={`Upgrade to ${upsellPlan.name}`}
                disabled={checkoutSlug !== null}
                onClick={() => onUpgrade(upsellPlan.slug, upsellPlan.name)}
              >
                {checkoutSlug === upsellPlan.slug && <Loader2 className="size-3.5 animate-spin" />}
                Upgrade to {upsellPlan.name}
              </Button>
              <p className="mt-4 text-sm text-muted-foreground">
                Need something custom?{' '}
                <a href="mailto:support@hookmyapp.com?subject=Custom%20plan" className="font-medium underline underline-offset-4">
                  Talk to us.
                </a>
              </p>
            </>
          ) : (
            <>
              {/* Top of the v2 ladder: nothing higher to sell, so the honest
                  next step is a conversation. Mirrors the legacy Custom card. */}
              <p className="mt-1 text-sm text-muted-foreground">
                {sub.isCustomPlan
                  ? 'Your plan is tailored to you. Talk to us to change it.'
                  : 'You are on our largest plan. We tailor quota and pricing above it.'}
              </p>
              <a
                href="mailto:support@hookmyapp.com?subject=More%20actions"
                className={cn(buttonVariants({ variant: 'outline', size: 'lg' }), 'mt-6')}
              >
                Talk To Us
              </a>
            </>
          )}
        </div>
      </div>
    );
  }

  // Nothing above matched: no subscription state we know how to describe
  // (e.g. a row still carrying the 'free' sentinel outside the canceled
  // path). Never an empty page — offer the plans (AIT-572).
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Pick a plan to get started.</p>
      <V2PlanGrid billingInterval={billingInterval} checkoutSlug={checkoutSlug} onAction={onCheckout} />
    </div>
  );
}
